const DEG = Math.PI / 180;
const rad = (deg: number) => deg * DEG;
const deg = (r: number) => r / DEG;

export const OFFICIAL_ZENITH = 90.833;

const HORIZON_ELEVATION = -(OFFICIAL_ZENITH - 90);

type SolarTerms = {
  declination: number;
  equationOfTime: number;
};

const julianCentury = (utcMs: number): number =>
  (utcMs / 86_400_000 + 2_440_587.5 - 2_451_545) / 36_525;

const solarTerms = (t: number): SolarTerms => {
  const meanLongitude = (280.46646 + t * (36_000.76983 + t * 0.0003032)) % 360;
  const meanAnomaly = 357.52911 + t * (35_999.05029 - 0.0001537 * t);
  const eccentricity = 0.016708634 - t * (0.000042037 + 0.0000001267 * t);
  const m = rad(meanAnomaly);

  const center =
    Math.sin(m) * (1.914602 - t * (0.004817 + 0.000014 * t)) +
    Math.sin(2 * m) * (0.019993 - 0.000101 * t) +
    Math.sin(3 * m) * 0.000289;

  const omega = 125.04 - 1934.136 * t;
  const apparentLongitude =
    meanLongitude + center - 0.00569 - 0.00478 * Math.sin(rad(omega));

  const obliquityMean =
    23 +
    (26 + (21.448 - t * (46.815 + t * (0.00059 - t * 0.001813))) / 60) / 60;
  const obliquity = obliquityMean + 0.00256 * Math.cos(rad(omega));

  const declination = deg(
    Math.asin(Math.sin(rad(obliquity)) * Math.sin(rad(apparentLongitude))),
  );

  const y = Math.tan(rad(obliquity / 2)) ** 2;
  const l = rad(meanLongitude);
  const equationOfTime =
    4 *
    deg(
      y * Math.sin(2 * l) -
        2 * eccentricity * Math.sin(m) +
        4 * eccentricity * y * Math.sin(m) * Math.cos(2 * l) -
        0.5 * y * y * Math.sin(4 * l) -
        1.25 * eccentricity * eccentricity * Math.sin(2 * m),
    );

  return { declination, equationOfTime };
};

export const solarElevation = (
  lat: number,
  lon: number,
  utcMs: number,
): number => {
  const { declination, equationOfTime } = solarTerms(julianCentury(utcMs));
  const d = new Date(utcMs);
  const minutesUtc =
    d.getUTCHours() * 60 + d.getUTCMinutes() + d.getUTCSeconds() / 60;
  const trueSolarTime =
    (((minutesUtc + equationOfTime + 4 * lon) % 1440) + 1440) % 1440;
  const hourAngle = rad(trueSolarTime / 4 - 180);
  const la = rad(lat);
  const dc = rad(declination);

  return deg(
    Math.asin(
      Math.sin(la) * Math.sin(dc) +
        Math.cos(la) * Math.cos(dc) * Math.cos(hourAngle),
    ),
  );
};

export type SolarBounds =
  | {
      kind: "normal";
      sunriseMin: number;
      sunsetMin: number;
    }
  | { kind: "polar-day" }
  | { kind: "polar-night" };

export const solarBounds = (
  lat: number,
  lon: number,
  dayKey: string,
  utcOffsetSeconds: number,
): SolarBounds => {
  const [y, m, d] = dayKey.split("-").map(Number);
  if (!y || !m || !d) return { kind: "polar-day" };

  const noonUtcMs = Date.UTC(y, m - 1, d, 12, 0, 0);
  const { declination, equationOfTime } = solarTerms(julianCentury(noonUtcMs));

  const la = rad(lat);
  const dc = rad(declination);
  const cosHourAngle =
    (Math.cos(rad(OFFICIAL_ZENITH)) - Math.sin(la) * Math.sin(dc)) /
    (Math.cos(la) * Math.cos(dc));

  if (cosHourAngle > 1) return { kind: "polar-night" };
  if (cosHourAngle < -1) return { kind: "polar-day" };

  const hourAngle = deg(Math.acos(cosHourAngle));
  const offsetMin = utcOffsetSeconds / 60;

  return {
    kind: "normal",
    sunriseMin: 720 - 4 * (lon + hourAngle) - equationOfTime + offsetMin,
    sunsetMin: 720 - 4 * (lon - hourAngle) - equationOfTime + offsetMin,
  };
};

export const cityLocalNow = (
  utcOffsetSeconds: number,
  nowMs: number = Date.now(),
): { minuteOfDay: number; dayKey: string } => {
  const shifted = new Date(nowMs + utcOffsetSeconds * 1000);
  const pad = (n: number) => String(n).padStart(2, "0");
  return {
    minuteOfDay:
      shifted.getUTCHours() * 60 +
      shifted.getUTCMinutes() +
      shifted.getUTCSeconds() / 60,
    dayKey: `${shifted.getUTCFullYear()}-${pad(shifted.getUTCMonth() + 1)}-${pad(
      shifted.getUTCDate(),
    )}`,
  };
};

export const isNightWithin = (
  bounds: SolarBounds,
  minuteOfDay: number,
): boolean => {
  if (bounds.kind === "polar-day") return false;
  if (bounds.kind === "polar-night") return true;
  return !(minuteOfDay >= bounds.sunriseMin && minuteOfDay < bounds.sunsetMin);
};

export { HORIZON_ELEVATION };
